import React, { useRef, useState } from 'react';
import { toast } from 'sonner';
import { Sparkles, Type, X } from 'lucide-react';
import api, { errMsg, fileUrl } from '@/lib/api';
import { ImageDrop } from '@/components/ImageDrop';
import { useI18n } from '@/context/LanguageContext';
import { Textarea } from '@/components/ui/textarea';

export const Step3Prompt = ({ prompt, setPrompt, texts, setTexts, extras, setExtras, fidelity, setFidelity }) => {
  const { t } = useI18n();
  const [busy, setBusy] = useState(false);
  const taRef = useRef(null);

  const FIDELITY = [
    { id: 'exact', label: t('studio.fidelityExact'), desc: t('studio.fidelityExactDesc') },
    { id: 'creative', label: t('studio.fidelityCreative'), desc: t('studio.fidelityCreativeDesc') },
  ];

  const SUGGESTIONS = [t('step3.sug1'), t('step3.sug2'), t('step3.sug3'), t('step3.sug4')];

  const upload = async (files) => {
    if (extras.length >= 4) {
      toast.error(t('step3.extrasMax'));
      return;
    }
    setBusy(true);
    try {
      const added = [];
      for (const f of files.slice(0, 4 - extras.length)) {
        const fd = new FormData();
        fd.append('file', f);
        const { data } = await api.post('/uploads', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        added.push({ file_id: data.file_id, url: data.url });
      }
      setExtras([...extras, ...added]);
      toast.success(t('step3.extrasAdded', { count: added.length }));
    } catch (e) {
      toast.error(errMsg(e, t('step3.extrasFailed')));
    } finally {
      setBusy(false);
    }
  };

  const insertTag = (i) => {
    setPrompt((p) => (p ? `${p.replace(/\s*$/, '')} @${i + 1} ` : `@${i + 1} `));
    taRef.current?.focus();
  };

  return (
    <div className="space-y-7">
      {/* TEXTS */}
      <div>
        <div className="flex items-center gap-2">
          <Type className="h-4 w-4 text-[hsl(var(--brand-red))]" />
          <h2 className="font-display text-lg font-semibold">{t('step3.textsTitle')}</h2>
        </div>
        <p className="mt-1.5 text-sm text-[hsl(var(--text-muted))]">{t('step3.textsDesc')}</p>
        <Textarea
          data-testid="texts-textarea"
          value={texts}
          onChange={(e) => setTexts(e.target.value)}
          rows={3}
          placeholder={t('step3.textsPlaceholder')}
          className="mt-3 resize-y bg-[hsl(var(--surface-2))] text-sm leading-relaxed"
        />
      </div>

      <div className="h-px bg-[hsl(var(--border-soft))]" />

      {/* CUSTOM REQUEST */}
      <div>
        <h2 className="font-display text-lg font-semibold">{t('step3.promptTitle')}</h2>
        <p className="mt-1.5 text-sm text-[hsl(var(--text-muted))]">{t('step3.promptDesc')}</p>
        <Textarea
          ref={taRef}
          data-testid="prompt-textarea"
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          rows={5}
          placeholder={t('step3.promptPlaceholder')}
          className="mt-3 resize-y bg-[hsl(var(--surface-2))] text-sm leading-relaxed"
        />
        <div className="mt-2 flex flex-wrap gap-1.5">
          {SUGGESTIONS.map((sg) => (
            <button
              key={sg}
              type="button"
              onClick={() => setPrompt((p) => (p ? `${p.replace(/\s*$/, '')}. ${sg}` : sg))}
              className="rounded-full border border-[hsl(var(--border-soft))] bg-[hsl(var(--surface))] px-2.5 py-1 text-[11px] text-[hsl(var(--text-2))] transition-colors duration-150 hover:border-[hsl(var(--brand-red))] hover:text-white"
            >
              + {sg}
            </button>
          ))}
        </div>
      </div>

      {/* EXTRA IMAGES */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold">{t('step3.extrasTitle')}</h3>
          <span className="font-mono-alt text-[11px] text-[hsl(var(--text-muted))]">{extras.length}/4</span>
        </div>

        {extras.length > 0 && (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {extras.map((x, i) => (
              <div
                key={x.file_id}
                className="group relative overflow-hidden rounded-[12px] border border-[hsl(var(--border-soft))] bg-[hsl(var(--surface))]"
                style={{ aspectRatio: '1' }}
              >
                <img src={fileUrl(x.url)} alt={`@${i + 1}`} className="h-full w-full object-cover" />
                <button
                  type="button"
                  data-testid={`extra-image-chip-${i + 1}`}
                  onClick={() => insertTag(i)}
                  title={t('step3.addToPrompt')}
                  className="absolute left-1.5 top-1.5 rounded-full bg-[hsl(var(--brand-red))] px-2 py-0.5 font-mono-alt text-[11px] font-semibold text-white"
                >
                  @{i + 1}
                </button>
                <button
                  type="button"
                  data-testid={`extra-image-remove-${i + 1}`}
                  onClick={() => setExtras(extras.filter((e) => e.file_id !== x.file_id))}
                  className="absolute right-1.5 top-1.5 grid h-6 w-6 place-items-center rounded-full bg-black/75 text-white transition-colors duration-150 hover:bg-[hsl(var(--brand-red))]"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}

        {extras.length < 4 && (
          <ImageDrop
            testId="extra-images-uploader"
            compact
            multiple
            enablePaste={false}
            onFiles={upload}
            busy={busy}
            title={t('step3.extrasDropTitle')}
            hint={t('step3.extrasDropHint')}
          />
        )}
      </div>

      {/* FIDELITY */}
      <div className="space-y-3">
        <h3 className="text-sm font-semibold">{t('step3.fidelityTitle')}</h3>
        <div data-testid="reference-fidelity-selector" className="grid gap-2.5 sm:grid-cols-2">
          {FIDELITY.map((f) => {
            const sel = fidelity === f.id;
            return (
              <button
                key={f.id}
                type="button"
                data-testid={`fidelity-option-${f.id}`}
                onClick={() => setFidelity(f.id)}
                className={[
                  'rounded-[var(--radius-md)] border p-4 text-left transition-colors duration-150',
                  sel
                    ? 'border-[hsl(var(--brand-red))] bg-[hsl(var(--surface-2))] ring-1 ring-[hsl(var(--brand-red))]/40'
                    : 'border-[hsl(var(--border-soft))] bg-[hsl(var(--surface))] hover:border-[hsl(var(--border-strong))]',
                ].join(' ')}
              >
                <div className="flex items-center gap-2">
                  <span
                    className={[
                      'h-2.5 w-2.5 rounded-full',
                      sel ? 'bg-[hsl(var(--brand-red))]' : 'bg-[hsl(var(--border-strong))]',
                    ].join(' ')}
                  />
                  <span className="text-sm font-semibold">{f.label}</span>
                </div>
                <p className="mt-1.5 text-xs leading-relaxed text-[hsl(var(--text-muted))]">{f.desc}</p>
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex items-start gap-2 rounded-[var(--radius-md)] border border-[hsl(var(--border-soft))] bg-[hsl(var(--surface))] p-3">
        <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-[hsl(var(--brand-red))]" />
        <p className="text-xs leading-relaxed text-[hsl(var(--text-muted))]">{t('step3.tip')}</p>
      </div>
    </div>
  );
};
